import { NopPositionRow } from '@/components/trader-modules/Blotters/NopPositionTable';
import { formatInstrumentType } from '@/helpers/blotterHelper';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { store } from '@/state/store';
import useInterval from '@/utils/hooks/useInterval';
import BigNumber from 'bignumber.js';
import { useMemo, useState } from 'react';
import { NopPosition, Positions } from '../providers/PositionsProvider';
import { translatePair } from '../symbolMapping';
import { getUniversalInstrument } from './useInstrument';

interface UseNopPositionsProps {}

interface ReturnNopPositions {
    nopPositionsHydated: boolean;
    debouncedNopPositions: Positions;
    formattedNopPositions: NopPositionRow[];
}

const useNopPositions = (props: UseNopPositionsProps): ReturnNopPositions => {
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const [debouncedNopPositions, setDebouncedNopPositions] = useState<Positions>({
        cryptoCfd: [],
        fx: [],
        indexCfd: [],
        futureCfd: [],
        commodityCfd: [],
        baseCurrency: '',
        isHeartBeat: false
    });
    const [nopPositionsHydated, setNopPositionsHydated] = useState<boolean>(false);

    const currentAccountProperties = useMemo(
        () => credentials?.accounts.find((account) => account.code === currentAccount)?.properties || [],
        [credentials, currentAccount]
    );

    const accountType = useMemo(
        () => currentAccountProperties.find((property) => property.key === 'TYPE')?.value as 'NOP' | 'MARGIN',
        [currentAccountProperties]
    );
    const formattedNopPositions: any[] = useMemo(() => {
        const positions = [
            ...(debouncedNopPositions?.cryptoCfd || []),
            ...(debouncedNopPositions?.fx || []),
            ...(debouncedNopPositions?.indexCfd || []),
            ...(debouncedNopPositions?.commodityCfd || []),
            ...(debouncedNopPositions?.futureCfd || [])
        ] as NopPosition[];

        const baseCurrency = debouncedNopPositions.baseCurrency || '';

        const returnValues = positions.map((position: any) => {
            let instrumentName = position?.instrumentName || '';
            let showInstrumentName = instrumentName;
            let tableInstrument = translatePair(instrumentName);
            const instrumentConfig = getUniversalInstrument(instrumentName);

            if (instrumentConfig?.type === 'Future') {
                instrumentName = instrumentConfig?.tsSymbol;
                showInstrumentName = instrumentConfig?.gcexSymbol;
                tableInstrument = instrumentConfig?.gcexSymbol;
            }
            const bidAsk = store.getState().marketPair.bidAsk[instrumentName || ''];
            let currentPrice = bidAsk ? BigNumber(bidAsk.ask).plus(bidAsk.bid).div(2).toNumber() : '-';

            let unrealisedPnlValue = 0;
            if (currentPrice === '-' && position.currentPrice) {
                currentPrice = position.currentPrice;
            }
            if (currentPrice !== '-' && position?.amount) {
                unrealisedPnlValue = BigNumber(position.amount)
                    .multipliedBy(BigNumber(currentPrice).minus(position?.openPrice || 0))
                    .toNumber();
            }

            const market = {
                celer: instrumentConfig?.tsSymbol || position?.instrumentName || '',
                netdania: position?.instrumentName || '',
                show: tableInstrument
            } as PairMap;

            const totalUnrealisedPnl = BigNumber(position?.realizedProfitLoss || 0)
                .plus(unrealisedPnlValue)
                .toNumber();

            let convertedTotalUnrealisedPnl;

            // Conversion rate to base currency
            const [base, quote] = market.celer.split('/');
            if (market.celer.includes(baseCurrency)) {
                if (quote === baseCurrency) {
                    // If the quote currency is the base currency, we don't need to convert
                    convertedTotalUnrealisedPnl = totalUnrealisedPnl;
                } else if (base === baseCurrency) {
                    // If the base currency is not the quote, we need to convert
                    const conversionRate = currentPrice;
                    convertedTotalUnrealisedPnl = BigNumber(totalUnrealisedPnl).dividedBy(conversionRate).toNumber();
                }
            } else {
                // If the market does not include the base currency, we find the conversion rate through relative instruments

                // Find all instruments that have the base currency in their name
                const availableInstruments = Object.keys(store.getState().marketPair.bidAsk);

                const relativeBaseInstruments = availableInstruments.filter(
                    (instrument) => instrument.includes(base) && instrument.includes(baseCurrency)
                );
                const relativeQuoteInstruments = availableInstruments.filter(
                    (instrument) => instrument.includes(quote) && instrument.includes(baseCurrency)
                );
                if (relativeBaseInstruments.length > 0) {
                    // If we have relative instruments, we can convert the unrealised PnL
                    // In case of AUD/CAD the result could be either AUD/USD or USD/AUD
                    // We need to find the conversion rate based on the current price of the relative instrument
                    // Such that USD/AUD * AUD/CAD = USD/CAD

                    const relativeInstrument = relativeBaseInstruments[0];
                    const bidAsk = store.getState().marketPair.bidAsk[relativeInstrument];
                    const midPrice = bidAsk ? BigNumber(bidAsk.ask).plus(bidAsk.bid).div(2).toNumber() : '-';

                    const splitInstrument = relativeInstrument.split('/');
                    const relativeBase = splitInstrument[0];
                    const relativeQuote = splitInstrument[1];

                    if (relativeBase === baseCurrency) {
                        // If the relative base is the base currency, we can convert the unrealised PnL directly
                        convertedTotalUnrealisedPnl = BigNumber(totalUnrealisedPnl)
                            .dividedBy(midPrice)
                            .multipliedBy(BigNumber(1).dividedBy(currentPrice))
                            .toNumber();
                    } else if (relativeQuote === baseCurrency) {
                        // If the relative quote is the base currency, we need to invert the conversion rate
                        convertedTotalUnrealisedPnl = BigNumber(totalUnrealisedPnl)
                            .dividedBy(BigNumber(1).dividedBy(midPrice))
                            .multipliedBy(BigNumber(1).dividedBy(currentPrice))
                            .toNumber();
                    }
                } else if (relativeQuoteInstruments.length > 0) {
                    // If we have relative instruments, we can convert the unrealised PnL
                    // In case of AUD/CAD the result could be either CAD/USD or USD/CAD
                    // We need to find the conversion rate based on the current price of the relative instrument
                    // Such that AUD/CAD * CAD/USD = AUD/USD

                    const relativeInstrument = relativeQuoteInstruments[0];
                    const bidAsk = store.getState().marketPair.bidAsk[relativeInstrument];
                    const midPrice = bidAsk ? BigNumber(bidAsk.ask).plus(bidAsk.bid).div(2).toNumber() : '-';

                    const splitInstrument = relativeInstrument.split('/');
                    const relativeBase = splitInstrument[0];
                    const relativeQuote = splitInstrument[1];

                    if (relativeBase === baseCurrency) {
                        // If the relative base is the base currency, we can convert the unrealised PnL directly
                        convertedTotalUnrealisedPnl = BigNumber(totalUnrealisedPnl).dividedBy(midPrice).toNumber();
                    } else if (relativeQuote === baseCurrency) {
                        // If the relative quote is the base currency, we need to invert the conversion rate
                        convertedTotalUnrealisedPnl = BigNumber(totalUnrealisedPnl)
                            .dividedBy(BigNumber(1).dividedBy(midPrice))
                            .toNumber();
                    }
                } else {
                    // If we don't have any relative instruments, we can't convert the unrealised PnL
                    if (
                        position?.realizedProfitLossInBaseCurrency !== undefined &&
                        position?.unrealizedProfitLossInBaseCurrency !== undefined
                    ) {
                        convertedTotalUnrealisedPnl = BigNumber(position?.realizedProfitLossInBaseCurrency || 0)
                            .plus(position?.unrealizedProfitLossInBaseCurrency || 0)
                            .toNumber();
                    } else {
                        convertedTotalUnrealisedPnl = null;
                    }
                }
            }

            return {
                ...position,
                type: formatInstrumentType(instrumentConfig, accountType) || '',
                instrument: showInstrumentName,
                market,
                tableInstrument,
                openPrice: position?.openPrice || 0,
                unrealisedPnl: {
                    value: totalUnrealisedPnl,
                    currency: baseCurrency
                },
                unrealisedPnlInBaseCurrency: {
                    value: convertedTotalUnrealisedPnl || totalUnrealisedPnl,
                    currency: baseCurrency
                },
                unsettledPnL: {
                    value: position?.realizedProfitLoss || 0,
                    currency: baseCurrency
                },
                unsettledPnLInBaseCurrency: {
                    value: position?.realizedProfitLossInBaseCurrency || 0,
                    currency: baseCurrency
                },
                ccy1: instrumentConfig?.ccy1,
                ccy2: instrumentConfig?.ccy2,
                currentPrice,
                settlementDate: position?.valueDate || '-',
                accountCode: currentAccount,
                tsSymbol: instrumentConfig?.tsSymbol || ''
            };
        }) as NopPositionRow[];

        return returnValues;
    }, [debouncedNopPositions, currentAccount, accountType]);

    useInterval(
        () => {
            const nopPositions = store.getState().nopPosition.nopPositions;
            setDebouncedNopPositions({ ...nopPositions });

            const aggregatedPositions = [
                ...(nopPositions?.cryptoCfd || []),
                ...(nopPositions?.fx || []),
                ...(nopPositions?.indexCfd || []),
                ...(nopPositions?.commodityCfd || []),
                ...(nopPositions?.futureCfd || [])
            ];

            const aggregatedDebouncedNopPositions = [
                ...(debouncedNopPositions?.cryptoCfd || []),
                ...(debouncedNopPositions?.fx || []),
                ...(debouncedNopPositions?.indexCfd || []),
                ...(debouncedNopPositions?.commodityCfd || []),
                ...(debouncedNopPositions?.futureCfd || [])
            ];

            if (aggregatedDebouncedNopPositions.length === 0 && aggregatedPositions.length !== 0) {
                // Run this every time when open positions are hydrated in the table.
                // Doesn't matter if this state is true or false, we just need it to trigger the useEffect above
                setNopPositionsHydated(!nopPositionsHydated);
            }
        },
        1000,
        true
    );

    return { nopPositionsHydated, debouncedNopPositions, formattedNopPositions };
};

export default useNopPositions;
